import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas";

export const DEFAULT_TERMINAL_ID = "default";

const TrimmedNonEmptyStringSchema = TrimmedNonEmptyString;
const TerminalColsSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(20)).check(
  Schema.isLessThanOrEqualTo(400),
);
const TerminalRowsSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(5)).check(
  Schema.isLessThanOrEqualTo(200),
);
const TerminalStreamCursorSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const TerminalControlSequenceSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));
const TerminalIdSchema = TrimmedNonEmptyStringSchema.check(Schema.isMaxLength(128));
const TerminalSessionEpochSchema = TrimmedNonEmptyStringSchema.check(Schema.isMaxLength(128));
const TerminalControllerIdSchema = TrimmedNonEmptyStringSchema.check(Schema.isMaxLength(128));
const TerminalEnvKeySchema = Schema.String.check(
  Schema.isPattern(/^[A-Za-z_][A-Za-z0-9_]*$/),
).check(Schema.isMaxLength(128));
const TerminalEnvValueSchema = Schema.String.check(Schema.isMaxLength(8_192));
const TerminalEnvSchema = Schema.Record(TerminalEnvKeySchema, TerminalEnvValueSchema).check(
  Schema.isMaxProperties(128),
);

const TerminalIdWithDefaultSchema = TerminalIdSchema.pipe(
  Schema.withDecodingDefault(Effect.succeed(DEFAULT_TERMINAL_ID)),
);

export const TerminalThreadInput = Schema.Struct({
  threadId: TrimmedNonEmptyStringSchema,
});
export type TerminalThreadInput = typeof TerminalThreadInput.Type;

const TerminalSessionInput = Schema.Struct({
  ...TerminalThreadInput.fields,
  terminalId: TerminalIdWithDefaultSchema,
});
export type TerminalSessionInput = Schema.Codec.Encoded<typeof TerminalSessionInput>;

export const TerminalOpenInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  cwd: TrimmedNonEmptyStringSchema,
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyStringSchema)),
  cols: Schema.optional(TerminalColsSchema),
  rows: Schema.optional(TerminalRowsSchema),
  env: Schema.optional(TerminalEnvSchema),
});
export type TerminalOpenInput = Schema.Codec.Encoded<typeof TerminalOpenInput>;

/**
 * Open-or-attach in one call, and then stream the session.
 *
 * Exists because the mobile client's terminal screen has no separate open step:
 * it subscribes and expects the buffer plus every later event on one stream. The
 * fields mirror `TerminalOpenInput`, except `cwd` is optional (an attach to a
 * session that already exists does not need to say where it lives) and
 * `restartIfNotRunning` is explicit, because attaching to a dead shell must not
 * silently respawn it — that is how a user who types `exit` gets a new shell
 * instead of leaving the screen.
 */
export const TerminalAttachInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  cwd: Schema.optional(TrimmedNonEmptyStringSchema),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyStringSchema)),
  cols: Schema.optional(TerminalColsSchema),
  rows: Schema.optional(TerminalRowsSchema),
  env: Schema.optional(TerminalEnvSchema),
  restartIfNotRunning: Schema.optional(Schema.Boolean),
});
export type TerminalAttachInput = Schema.Codec.Encoded<typeof TerminalAttachInput>;

export const TerminalWriteInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  data: Schema.String.check(Schema.isNonEmpty()).check(Schema.isMaxLength(65_536)),
});
export type TerminalWriteInput = Schema.Codec.Encoded<typeof TerminalWriteInput>;

export const TerminalControlIdentity = Schema.Struct({
  sessionEpoch: TerminalSessionEpochSchema,
  controllerId: TerminalControllerIdSchema,
  controlSequence: TerminalControlSequenceSchema,
});
export type TerminalControlIdentity = typeof TerminalControlIdentity.Type;

export const TerminalWriteRequest = Schema.Struct({
  ...TerminalWriteInput.fields,
  ...TerminalControlIdentity.fields,
});
export type TerminalWriteRequest = typeof TerminalWriteRequest.Type;

export const TerminalResizeInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  cols: TerminalColsSchema,
  rows: TerminalRowsSchema,
});
export type TerminalResizeInput = Schema.Codec.Encoded<typeof TerminalResizeInput>;

export const TerminalResizeRequest = Schema.Struct({
  ...TerminalResizeInput.fields,
  ...TerminalControlIdentity.fields,
});
export type TerminalResizeRequest = typeof TerminalResizeRequest.Type;

export const TerminalSignal = Schema.Literals(["SIGINT", "SIGTERM", "SIGHUP", "SIGQUIT"]);
export type TerminalSignal = typeof TerminalSignal.Type;

export const TerminalSignalInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  signal: TerminalSignal,
});
export type TerminalSignalInput = Schema.Codec.Encoded<typeof TerminalSignalInput>;

export const TerminalSignalRequest = Schema.Struct({
  ...TerminalSignalInput.fields,
  ...TerminalControlIdentity.fields,
});
export type TerminalSignalRequest = typeof TerminalSignalRequest.Type;

export const TerminalClearInput = TerminalSessionInput;
export type TerminalClearInput = Schema.Codec.Encoded<typeof TerminalClearInput>;

export const TerminalClearRequest = Schema.Struct({
  ...TerminalClearInput.fields,
  ...TerminalControlIdentity.fields,
});
export type TerminalClearRequest = typeof TerminalClearRequest.Type;

export const TerminalRestartInput = Schema.Struct({
  ...TerminalSessionInput.fields,
  cwd: TrimmedNonEmptyStringSchema,
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyStringSchema)),
  cols: TerminalColsSchema,
  rows: TerminalRowsSchema,
  env: Schema.optional(TerminalEnvSchema),
});
export type TerminalRestartInput = Schema.Codec.Encoded<typeof TerminalRestartInput>;

export const TerminalRestartRequest = Schema.Struct({
  ...TerminalRestartInput.fields,
  ...TerminalControlIdentity.fields,
});
export type TerminalRestartRequest = typeof TerminalRestartRequest.Type;

export const TerminalCloseInput = Schema.Struct({
  ...TerminalThreadInput.fields,
  terminalId: Schema.optional(TerminalIdSchema),
  deleteHistory: Schema.optional(Schema.Boolean),
});
export type TerminalCloseInput = typeof TerminalCloseInput.Type;

export const TerminalCloseRequest = Schema.Struct({
  ...TerminalSessionInput.fields,
  ...TerminalControlIdentity.fields,
  deleteHistory: Schema.optional(Schema.Boolean),
});
export type TerminalCloseRequest = typeof TerminalCloseRequest.Type;

export const TerminalControlAck = Schema.Struct({
  ...TerminalSessionInput.fields,
  ...TerminalControlIdentity.fields,
  disposition: Schema.Literals(["applied", "duplicate"]),
});
export type TerminalControlAck = typeof TerminalControlAck.Type;

export const TerminalSessionStatus = Schema.Literals(["starting", "running", "exited", "error"]);
export type TerminalSessionStatus = typeof TerminalSessionStatus.Type;

export const TerminalSessionSnapshot = Schema.Struct({
  threadId: Schema.String.check(Schema.isNonEmpty()),
  terminalId: Schema.String.check(Schema.isNonEmpty()),
  sessionEpoch: Schema.optional(Schema.String.check(Schema.isNonEmpty())),
  eventSequence: Schema.optional(TerminalStreamCursorSchema),
  outputCursor: Schema.optional(TerminalStreamCursorSchema),
  historyTruncated: Schema.optional(Schema.Boolean),
  cwd: Schema.String.check(Schema.isNonEmpty()),
  worktreePath: Schema.NullOr(TrimmedNonEmptyStringSchema),
  status: TerminalSessionStatus,
  pid: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
  history: Schema.String,
  exitCode: Schema.NullOr(Schema.Int),
  exitSignal: Schema.NullOr(Schema.Int),
  updatedAt: Schema.String,
});
export type TerminalSessionSnapshot = typeof TerminalSessionSnapshot.Type;

export const TerminalRestartResult = Schema.Struct({
  ack: TerminalControlAck,
  snapshot: TerminalSessionSnapshot,
});
export type TerminalRestartResult = typeof TerminalRestartResult.Type;

const TerminalEventBaseSchema = Schema.Struct({
  threadId: Schema.String.check(Schema.isNonEmpty()),
  terminalId: Schema.String.check(Schema.isNonEmpty()),
  sessionEpoch: Schema.optional(Schema.String.check(Schema.isNonEmpty())),
  sequence: Schema.optional(TerminalStreamCursorSchema),
  createdAt: Schema.String,
});

const TerminalStartedEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("started"),
  snapshot: TerminalSessionSnapshot,
});

const TerminalOutputEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("output"),
  data: Schema.String,
  startCursor: Schema.optional(TerminalStreamCursorSchema),
  endCursor: Schema.optional(TerminalStreamCursorSchema),
});

const TerminalExitedEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("exited"),
  exitCode: Schema.NullOr(Schema.Int),
  exitSignal: Schema.NullOr(Schema.Int),
});

const TerminalErrorEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("error"),
  message: Schema.String.check(Schema.isNonEmpty()),
});

const TerminalClearedEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("cleared"),
});

const TerminalRestartedEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("restarted"),
  snapshot: TerminalSessionSnapshot,
});

const TerminalActivityEvent = Schema.Struct({
  ...TerminalEventBaseSchema.fields,
  type: Schema.Literal("activity"),
  hasRunningSubprocess: Schema.Boolean,
});

export const TerminalEvent = Schema.Union([
  TerminalStartedEvent,
  TerminalOutputEvent,
  TerminalExitedEvent,
  TerminalErrorEvent,
  TerminalClearedEvent,
  TerminalRestartedEvent,
  TerminalActivityEvent,
]);

/**
 * The session's current state, sent once when an attach stream opens.
 *
 * Distinct from `started`: `started` means a pty was spawned, whereas this says
 * "here is the buffer to render", and is sent whether the session was just
 * created, was already running, or has already exited.
 */
const TerminalAttachSnapshotEvent = Schema.Struct({
  type: Schema.Literal("snapshot"),
  snapshot: TerminalSessionSnapshot,
});

/**
 * What `terminal.attach` streams: one snapshot, then this session's own events.
 *
 * `started` is deliberately absent. The manager emits it both when it spawns a pty
 * and in the replay it sends every new subscriber, and an attach stream reports
 * both as `snapshot` — so a client has exactly one event meaning "reset your buffer
 * to this" and never has to tell "already running" apart from "just started".
 * `restarted` stays, because losing a pty mid-session is worth distinguishing.
 */
export const TerminalAttachStreamEvent = Schema.Union([
  TerminalAttachSnapshotEvent,
  TerminalOutputEvent,
  TerminalExitedEvent,
  TerminalErrorEvent,
  TerminalClearedEvent,
  TerminalRestartedEvent,
  TerminalActivityEvent,
]);
export type TerminalAttachStreamEvent = typeof TerminalAttachStreamEvent.Type;
export type TerminalEvent = typeof TerminalEvent.Type;

export class TerminalCwdError extends Schema.TaggedErrorClass<TerminalCwdError>()(
  "TerminalCwdError",
  {
    cwd: Schema.String,
    reason: Schema.Literals(["notFound", "notDirectory", "statFailed"]),
    cause: Schema.optional(Schema.Defect),
  },
) {
  override get message() {
    if (this.reason === "notDirectory") {
      return `Terminal cwd is not a directory: ${this.cwd}`;
    }
    if (this.reason === "notFound") {
      return `Terminal cwd does not exist: ${this.cwd}`;
    }
    const causeMessage =
      this.cause && typeof this.cause === "object" && "message" in this.cause
        ? this.cause.message
        : undefined;
    return causeMessage
      ? `Failed to access terminal cwd: ${this.cwd} (${causeMessage})`
      : `Failed to access terminal cwd: ${this.cwd}`;
  }
}

export class TerminalHistoryError extends Schema.TaggedErrorClass<TerminalHistoryError>()(
  "TerminalHistoryError",
  {
    operation: Schema.Literals(["read", "truncate", "migrate"]),
    threadId: Schema.String,
    terminalId: Schema.String,
    cause: Schema.optional(Schema.Defect),
  },
) {
  override get message() {
    return `Failed to ${this.operation} terminal history for thread: ${this.threadId}, terminal: ${this.terminalId}`;
  }
}

export class TerminalSessionLookupError extends Schema.TaggedErrorClass<TerminalSessionLookupError>()(
  "TerminalSessionLookupError",
  {
    threadId: Schema.String,
    terminalId: Schema.String,
  },
) {
  override get message() {
    return `Unknown terminal thread: ${this.threadId}, terminal: ${this.terminalId}`;
  }
}

export class TerminalNotRunningError extends Schema.TaggedErrorClass<TerminalNotRunningError>()(
  "TerminalNotRunningError",
  {
    threadId: Schema.String,
    terminalId: Schema.String,
  },
) {
  override get message() {
    return `Terminal is not running for thread: ${this.threadId}, terminal: ${this.terminalId}`;
  }
}

export class TerminalSessionEpochError extends Schema.TaggedErrorClass<TerminalSessionEpochError>()(
  "TerminalSessionEpochError",
  {
    threadId: Schema.String,
    terminalId: Schema.String,
    expectedSessionEpoch: Schema.String,
    receivedSessionEpoch: Schema.String,
  },
) {
  override get message() {
    return `Control targeted a stale terminal session for thread: ${this.threadId}, terminal: ${this.terminalId}`;
  }
}

export class TerminalControlSequenceError extends Schema.TaggedErrorClass<TerminalControlSequenceError>()(
  "TerminalControlSequenceError",
  {
    threadId: Schema.String,
    terminalId: Schema.String,
    controllerId: Schema.String,
    expectedControlSequence: TerminalControlSequenceSchema,
    receivedControlSequence: TerminalControlSequenceSchema,
    reason: Schema.Literals(["gap", "stale", "conflict"]),
  },
) {
  override get message() {
    return `Invalid terminal control sequence for thread: ${this.threadId}, terminal: ${this.terminalId}; expected ${this.expectedControlSequence}, received ${this.receivedControlSequence}`;
  }
}

export class TerminalSignalError extends Schema.TaggedErrorClass<TerminalSignalError>()(
  "TerminalSignalError",
  {
    threadId: Schema.String,
    terminalId: Schema.String,
    signal: TerminalSignal,
    cause: Schema.optional(Schema.Defect),
  },
) {
  override get message() {
    return `Failed to deliver ${this.signal} to terminal for thread: ${this.threadId}, terminal: ${this.terminalId}`;
  }
}

export const TerminalError = Schema.Union([
  TerminalCwdError,
  TerminalHistoryError,
  TerminalSessionLookupError,
  TerminalNotRunningError,
  TerminalSessionEpochError,
  TerminalControlSequenceError,
  TerminalSignalError,
]);
export type TerminalError = typeof TerminalError.Type;
